/**
 * NotificationBell — sino com badge de não-lidas + dropdown.
 *
 * - Polling a cada 60s pra atualizar o count (também usa Realtime via Supabase)
 * - Click no sino abre dropdown com últimas 50 notificações
 * - Click numa notificação: marca lida + navega pro link
 * - "Marcar todas como lidas" no header do dropdown
 * - "Ver todas" no footer → /notificacoes
 *
 * Realtime: escuta INSERT na tabela `notificacoes` filtrado pelo user_id atual.
 * Quando chega notificação, incrementa count e atualiza a lista.
 */
"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { getSupabase } from "@/lib/supabase";
import { useTheme } from "@/components/ThemeProvider";
import { formatRelativeTime } from "@/lib/datetime";


// ─── Types ────────────────────────────────────────────────────────────────
export interface Notificacao {
  id:        string;
  categoria: "trial" | "conta" | "empresa" | "plataforma" | "manual";
  tipo:      string;
  titulo:    string;
  mensagem:  string;
  link:      string | null;
  icone:     string | null;
  html:      string | null;
  dados:     Record<string, any>;
  lida:      boolean;
  lida_em:   string | null;
  criado_em: string;
}


// ─── Helpers ──────────────────────────────────────────────────────────────
const CATEGORIA_COLOR: Record<string, string> = {
  trial:      "bg-amber-100 text-amber-700",
  conta:      "bg-blue-100 text-blue-700",
  empresa:    "bg-emerald-100 text-emerald-700",
  plataforma: "bg-rose-100 text-rose-700",
  manual:     "bg-violet-100 text-violet-700",
};

// Categoria → chave i18n (no Bell usamos manualBell, na Page usamos manualPage)
const CATEGORIA_KEY: Record<string, string> = {
  trial:      "notifications.category.trial",
  conta:      "notifications.category.conta",
  empresa:    "notifications.category.empresa",
  plataforma: "notifications.category.plataforma",
  manual:     "notifications.category.manualBell",
};


// ─── Props ────────────────────────────────────────────────────────────────
interface BellProps {
  userId: string;  // pra subscribe do realtime
}


// ═══════════════════════════════════════════════════════════════════════════
export default function NotificationBell({ userId }: BellProps) {
  const { t } = useTranslation();
  const { locale } = useTheme();
  const router    = useRouter();
  const [open, setOpen]               = useState(false);
  const [count, setCount]             = useState(0);
  const [items, setItems]             = useState<Notificacao[]>([]);
  const [loading, setLoading]         = useState(false);
  const wrapperRef                    = useRef<HTMLDivElement>(null);

  // ─── Fetch helpers ──────────────────────────────────────────────────
  const fetchCount = useCallback(async () => {
    try {
      const { data } = await api.get<{ nao_lidas: number }>("/me/notificacoes/count");
      setCount(data.nao_lidas);
    } catch { /* silencioso */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get<{ items: Notificacao[]; nao_lidas: number }>(
        "/me/notificacoes?limit=20"
      );
      setItems(data.items);
      setCount(data.nao_lidas);
    } catch { /* silencioso */ }
    finally { setLoading(false); }
  }, []);

  // ─── Boot: fetch inicial + polling de fallback ──────────────────────
  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 60_000);
    return () => clearInterval(t);
  }, [fetchCount]);

  // ─── Realtime via Supabase ──────────────────────────────────────────
  // userId é a única dep — fetchCount é capturado via closure. Se mudar userId
  // (logout/login), canal é desmontado e recriado.
  useEffect(() => {
    if (!userId) return;
    const supabase = getSupabase();

    const channel = supabase
      .channel(`notif_${userId}_${Math.random().toString(36).slice(2, 9)}`)
      .on(
        "postgres_changes",
        {
          event:  "INSERT",
          schema: "public",
          table:  "notificacoes",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const novo = payload.new as Notificacao;
          setItems(prev => [novo, ...prev].slice(0, 20));
          setCount(c => c + 1);
        },
      )
      .on(
        "postgres_changes",
        {
          event:  "UPDATE",
          schema: "public",
          table:  "notificacoes",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const updated = payload.new as Notificacao;
          setItems(prev => {
            const idx = prev.findIndex(n => n.id === updated.id);
            if (idx >= 0) {
              const next = [...prev];
              next[idx] = updated;
              return next;
            }
            return prev;
          });
          // Re-fetch inline (sem closure de fetchCount)
          api.get<{ nao_lidas: number }>("/me/notificacoes/count")
            .then(({ data }) => setCount(data.nao_lidas))
            .catch(() => {});
        },
      )
      .on(
        "postgres_changes",
        {
          event:  "DELETE",
          schema: "public",
          table:  "notificacoes",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const oldId = (payload.old as { id?: string }).id;
          if (oldId) {
            setItems(prev => prev.filter(n => n.id !== oldId));
            api.get<{ nao_lidas: number }>("/me/notificacoes/count")
              .then(({ data }) => setCount(data.nao_lidas))
              .catch(() => {});
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  // ─── Click outside fecha ────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // ─── Toggle dropdown — fetch ao abrir ───────────────────────────────
  function handleToggle() {
    if (!open) fetchList();
    setOpen(o => !o);
  }

  // ─── Ações ──────────────────────────────────────────────────────────
  async function marcarLida(id: string) {
    try {
      await api.post(`/me/notificacoes/${id}/lida`);
      setItems(prev => prev.map(n => n.id === id ? { ...n, lida: true } : n));
      setCount(c => Math.max(0, c - 1));
    } catch { /* silencioso */ }
  }

  async function marcarTodas() {
    try {
      await api.post("/me/notificacoes/marcar_todas");
      setItems(prev => prev.map(n => ({ ...n, lida: true })));
      setCount(0);
    } catch { /* silencioso */ }
  }

  function handleClickItem(n: Notificacao) {
    if (!n.lida) marcarLida(n.id);
    if (n.link) {
      setOpen(false);
      router.push(n.link);
    }
  }

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <div ref={wrapperRef} className="relative">
      <button
        onClick={handleToggle}
        className="relative p-1.5 rounded-lg text-app-3 hover:text-app-1 hover:bg-app-hover transition-colors"
        aria-label={t("notifications.bell.ariaLabel")}
        title={t("notifications.bell.ariaLabel")}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
        {count > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 w-[380px] max-w-[calc(100vw-2rem)] bg-app-surface rounded-xl shadow-2xl border border-app overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-app-subtle">
            <div>
              <p className="font-bold text-sm text-app-1">{t("notifications.bell.headerTitle")}</p>
              <p className="text-xs text-app-3">
                {count > 0
                  ? t("notifications.bell.headerUnread", { count })
                  : t("notifications.bell.headerNoneUnread")}
              </p>
            </div>
            {count > 0 && (
              <button
                onClick={marcarTodas}
                className="text-xs text-rose-600 hover:text-rose-700 font-medium"
              >
                {t("notifications.bell.markAllRead")}
              </button>
            )}
          </div>

          {/* Lista */}
          <div className="flex-1 max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center text-xs text-app-3">{t("notifications.bell.loading")}</div>
            ) : items.length === 0 ? (
              <div className="px-4 py-12 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-app-soft flex items-center justify-center text-2xl">
                  🎉
                </div>
                <p className="text-sm text-app-3 font-medium">{t("notifications.bell.emptyTitle")}</p>
                <p className="text-xs text-app-3 mt-0.5">{t("notifications.bell.emptyDesc")}</p>
              </div>
            ) : (
              items.map(n => (
                <button
                  key={n.id}
                  onClick={() => handleClickItem(n)}
                  className={`w-full text-left px-4 py-3 border-b border-stone-50 hover:bg-app-hover transition-colors flex gap-3 ${n.lida ? "" : "bg-rose-50/40"}`}
                >
                  <div className="shrink-0 w-9 h-9 rounded-full bg-app-soft flex items-center justify-center text-base">
                    {n.icone || "🔔"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${CATEGORIA_COLOR[n.categoria] || CATEGORIA_COLOR.manual}`}>
                        {t(CATEGORIA_KEY[n.categoria] || `notifications.category.${n.categoria}`, { defaultValue: n.categoria })}
                      </span>
                      <span className="text-[10px] text-app-3">{formatRelativeTime(n.criado_em, locale, t)}</span>
                      {!n.lida && <span className="w-2 h-2 rounded-full bg-rose-600 ml-auto" />}
                    </div>
                    <p className="text-sm font-semibold text-app-1 leading-tight truncate">{n.titulo}</p>
                    <p className="text-xs text-app-3 mt-0.5 line-clamp-2">{n.mensagem}</p>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-app-subtle px-4 py-2.5 text-center">
            <Link
              href="/notificacoes"
              onClick={() => setOpen(false)}
              className="text-xs text-app-2 hover:text-app-1 font-medium"
            >
              {t("notifications.bell.viewAll")}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}